import type { Filter } from "./filter";

interface category {
    id: string;
    categoryId: number;
    name: string;
    image?: string;
    parentId?: number;
    length?: number;
    q?: string;
}

export interface Category extends category {
    product: number;
    brands: number[];
    filters: Filter[]
}

export function categoryArray(fullName: string) {
    return fullName.split(" > ");
}

export function shortName(fullName: string): string {
    return categoryArray(fullName).pop()!;
}

export function ancestors(
    all: Category[],
    category: Category,
): Category[] {
    const parent = all.find(x => x.categoryId === category.parentId);
    if(parent !== undefined) {
        return [...ancestors(all, parent), parent]
    }
    else {
        return [];
    }
}

export interface CategoryTree extends category {
    categories: Set<number>;
    brands: Set<number>;
    children: Map<string, CategoryTree>;
    isLeafInInput: boolean;
};

function buildTree(node: CategoryTree): CategoryTree {
    const children = new Map<string, CategoryTree>();
    for (const [name, childNode] of node.children) {
        const res = buildTree(childNode);
        if (res.categoryId) {
            children.set(name, res);
        } else {
            res.children.forEach(x => children.set(x.name, x));
        }
    }
    if (!node.isLeafInInput && children.size <= 1) {
        return { children } as CategoryTree;
    }
    return { ...node, children };
}

export function makeLikeATree(i: Category[]): Map<string, CategoryTree> {  
    const rootChildren = new Map<string, CategoryTree>();

    for (const { categoryId, brands, image, name } of i) {
        const path = name.split(" > ");
        let currentMap = rootChildren;
        for (let j = 0; j < path.length; j++) {
            const name = path[j];
            let node = currentMap.get(name);
            if (!node) {
                node = { id: `${categoryId}`, categoryId, name, categories: new Set(), children: new Map(), isLeafInInput: false, brands: new Set(), image };
                currentMap.set(name, node);
            }
            node.categories.add(categoryId);
            brands.forEach(brandId => node.brands.add(brandId));
            if (j === path.length - 1) {
                node.isLeafInInput = true;
            }
            currentMap = node.children;
        }
    }

    const children = new Map<string, CategoryTree>();
    for (const [name, node] of rootChildren) {
        const res = buildTree(node);
        if (res.categoryId) {
            children.set(name, res);
        } else {
            res.children.forEach(x => children.set(x.name, x));
        }
    }
    return children;
}

export function find(tree: CategoryTree, categories: Set<number>): CategoryTree | undefined {
    if (tree.categories.isSubsetOf(categories) && categories.isSubsetOf(tree.categories)) {
        return tree;
    } else {
        for (const [_, child] of tree.children) {
            const r = find(child, categories);
            if (r && r.categories.isSubsetOf(categories) && categories.isSubsetOf(r.categories)) {
                return r;
            }
        }
    }
}

export const transformCategory = (accountId: number) => (r: Category) => {
    const { name, image, filters } = r;
    return { name, image, filters: filters.map(x => ({ ...x, accountId })) };
};